import { t, Trans } from "@lingui/macro";
import * as Dialog from "@radix-ui/react-dialog";
import produce from "immer";
import { compressToEncodedURIComponent } from "lz-string";
import {
  Check,
  LinkSimple,
  Trash,
  X,
} from "phosphor-react";
import {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMutation, useQuery } from "react-query";

import { AUTH_IMG_SCALE } from "../lib/constants";
import { useDownloadFilename } from "../lib/hooks";
import {
  makeChartPublic,
  renderDiagram,
  storeChartSharedEmails,
} from "../lib/queries";
import { docToString, useDoc, useDocDetails } from "../lib/useDoc";
import { Box } from "../slang";
import { Close, Content, Overlay } from "../ui/Dialog";
import { Button2, Input } from "../ui/Shared";
import { Description, SectionTitle } from "../ui/Typography";
import { AppContext } from "./AppContextProvider";
import {
  downloadCanvas,
  downloadSvg,
  getCanvas,
  getSvg,
  svgToCanvas,
} from "./downloads";
import Loading from "./Loading";
import styles from "./ShareDialog.module.css";
import Spinner from "./Spinner";
import { useDiagramTypeTool } from "../lib/useDiagramTool";
import { FFTheme } from "../lib/FFTheme";

export default function ShareDialog({ children }: { children?: ReactNode }) {
  const isHosted = useDocDetails("isHosted");
  const { shareModal, setShareModal } = useContext(AppContext);
  const docString = useDoc(docToString);
  const shareLink = useMemo(() => {
    return compressToEncodedURIComponent(docString);
  }, [docString]);
  const fullscreen = `${new URL(window.location.href).origin}/f#${shareLink}`;
  const readOnly = `${new URL(window.location.href).origin}/c#${shareLink}`;
  const editable = `${new URL(window.location.href).origin}/#load:${shareLink}`;
  const filename = useDownloadFilename();
  const watermark = false;
  const scale = AUTH_IMG_SCALE;

  const [_, useKrokiChartView] = useDiagramTypeTool();
  const doc = useDoc.getState();

  async function outputDownload(fileType: string) {
    if (useKrokiChartView) {
      const svgData = await renderDiagram(
        doc.text,
        doc.meta.diagramTool as string,
        "svg",
        doc.meta.themeEditor as FFTheme
      );
      if (fileType === "png") {
        svgToCanvas({
          type: "png",
          svg: svgData,
          watermark,
          scale,
        })
          .then((canvas) =>
            downloadCanvas({
              ...canvas,
              filename,
            })
          )
          .catch(console.error);
      } else if (fileType === "jpg") {
        svgToCanvas({
          type: "jpg",
          svg: svgData,
          watermark,
          scale,
        })
          .then((canvas) =>
            downloadCanvas({
              ...canvas,
              filename,
            })
          )
          .catch(console.error);
      } else {
        downloadSvg({
          svg: svgData,
          filename,
        });
      }
    } else {
      if (!window.__cy) return;
      if (fileType === "png") {
        getCanvas({
          cy: window.__cy,
          type: "png",
          watermark,
          scale,
        })
          .then((canvas) =>
            downloadCanvas({
              ...canvas,
              filename,
            })
          )
          .catch(console.error);
      } else if (fileType === "jpg") {
        getCanvas({
          cy: window.__cy,
          type: "jpg",
          watermark,
          scale,
        }).then((canvas) =>
          downloadCanvas({
            ...canvas,
            filename,
          })
        );
      } else {
        const svg = await getSvg({
          cy: window.__cy,
        });
        downloadSvg({
          svg,
          filename,
        });
      }
    }
  }

  return (
    <Dialog.Root
      open={shareModal}
      onOpenChange={setShareModal}
      aria-label={t`Export`}
    >
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      <Overlay />
      <Content maxWidthClass="max-w-[600px] w-full" overflowV>
        <Close />
        <Column>
          <PreviewImage watermark={watermark} scale={scale} />
          <SectionTitle className="mb-1">
            <Trans>Download</Trans>
          </SectionTitle>
          <div className="grid gap-2 grid-cols-3">
            <Button2
              aria-label="Download PNG"
              onClick={async () => {
                await outputDownload("png");
              }}
            >
              PNG
            </Button2>
            <Button2
              aria-label="Download JPG"
              onClick={async () => {
                await outputDownload("jpg");
              }}
            >
              JPG
            </Button2>
            <Button2
              aria-label="Download SVG"
              onClick={async () => {
                await outputDownload("svg");
              }}
            >
              SVG
            </Button2>
          </div>
        </Column>
        {isHosted ? <HostedOptions /> : null}
        {
          <Column>
            <SectionTitle className="mb-1">
              <Trans>Link</Trans>
            </SectionTitle>
            <Box gap={4}>
              <LinkCopy
                value={fullscreen}
                title={t`Fullscreen`}
                rawTitle="Fullscreen"
              />
              <LinkCopy
                value={editable}
                title={t`Editable`}
                rawTitle="Editable"
              />
              <LinkCopy
                value={readOnly}
                title={t`Read-only`}
                rawTitle="Read-only"
              />
            </Box>
          </Column>
        }
      </Content>
    </Dialog.Root>
  );
}

function Title({ children }: { children: ReactNode }) {
  return <h3 className="text-lg font-bold mb-2">{children}</h3>;
}

function Column({ children }: { children: ReactNode }) {
  return (
    <Box className={styles.Column} gap={1} content="start normal">
      {children}
    </Box>
  );
}

function LinkCopy({
  value,
  title,
  rawTitle,
  ariaLabel = "",
}: {
  value: string;
  title: string;
  rawTitle: "Fullscreen" | "Editable" | "Read-only" | "Public";
  ariaLabel?: string;
}) {
  const [copied, setCopied] = useState(false);
  const Icon = copied ? Check : LinkSimple;
  useEffect(() => {
    if (copied) setTimeout(() => setCopied(false), 3000);
  }, [copied]);

  async function copyText() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
  }

  return (
    <Box gap={2}>
      <Description size="xs">{title}</Description>
      <Box
        template="auto / auto 1fr auto"
        items="stretch normal"
        className={styles.LinkCopy}
        rad={1}
        gap={1}
      >
        <div
          className={`grid place-items-center rounded w-8 ${
            copied ? "bg-green-500" : "bg-neutral-300 dark:bg-neutral-700"
          }`}
        >
          <Icon
            width={15}
            height={15}
            data-testid={copied ? `Copied ${rawTitle}` : ""}
            color={copied ? "white" : "black"}
          />
        </div>
        <input
          aria-label={ariaLabel}
          type="text"
          value={value}
          readOnly
          className={`text-[12px] px-2 font-mono text-neutral-500 dark:text-neutral-400 border border-neutral-300 dark:border-neutral-700 rounded overflow-hidden bg-transparent`}
          onFocus={copyText}
          data-testid={`Copy ${rawTitle}`}
        />
        <Button2 onClick={copyText} aria-label={`${t`Copy`} ${title}`}>
          <Trans>Copy</Trans>
        </Button2>
      </Box>
    </Box>
  );
}

function PreviewImage({
  watermark,
  scale,
}: {
  watermark?: boolean;
  scale?: number;
}) {
  const [_, useKrokiChartView] = useDiagramTypeTool();
  const doc = useDoc.getState();

  const img = useQuery(
    ["previewImg"],
    async () => {
      if (useKrokiChartView) {
        const svgData = await renderDiagram(
          doc.text,
          doc.meta.diagramTool as string,
          "svg",
          doc.meta.themeEditor as FFTheme
        );
        const { canvas } = await svgToCanvas({
          type: "png",
          svg: svgData,
          watermark,
          scale,
        });
        return canvas.toDataURL();
      } else {
        if (!window.__cy) return "";
        const { canvas } = await getCanvas({
          type: "png",
          cy: window.__cy,
          watermark,
          scale,
        });
        return canvas.toDataURL();
      }
    },
    {
      cacheTime: 0,
      staleTime: 0,
      refetchOnMount: true,
    }
  );

  if (img.isLoading) return <Loading />;
  return (
    <div className="m-4 max-h-[400px] relative text-center">
      <img
        src={img.data}
        alt="Preview"
        className="shadow-lg rounded inline-block h-full"
      />
    </div>
  );
}

function HostedOptions() {
  const id = useDocDetails("id");
  if (typeof id !== "number") throw new Error("id is not a number");

  const isPublic = useDocDetails("isPublic");
  const publicId = useDocDetails("publicId");
  const currentSharedEmails = useDocDetails("sharedEmails", []);
  const inputEmailRef = useRef<null | HTMLInputElement>(null);
  const [openDuplicatedEmailDialog, setOpenDuplicatedEmailDialog] =
    useState<boolean>(false);
  const [openInvalidEmailDialog, setOpenInvalidEmailDialog] =
    useState<boolean>(false);

  const makePublic = useMutation(
    "makeChartPublic",
    async (isPublic: boolean) => makeChartPublic(id, isPublic),
    {
      onSuccess: (result) => {
        if (!result) return;
        useDoc.setState(
          (state) => {
            return produce(state, (draft) => {
              draft.details.isPublic = result.isPublic;
              if (result.publicId) draft.details.publicId = result.publicId;
            });
          },
          false,
          "HostedOptions/makePublic"
        );
      },
    }
  );

  const storeSharedEmails = useMutation(
    "storeSharedEmails",
    async (sharedEmails: string[]) => storeChartSharedEmails(id, sharedEmails),
    {
      onSuccess: (result) => {
        if (!result) return;

        useDoc.setState(
          (state) => {
            return produce(state, (draft) => {
              draft.details.sharedEmails = result.sharedEmails;
            });
          },
          false,
          "HostedOptions/storeSharedEmails"
        );
      },
    }
  );

  function isValidEmail(email: string) {
    return /\S+@\S+\.\S+/.test(email);
  }

  return (
    <Column>
      <Title>
        <Trans>Public</Trans>
      </Title>
      <Box flow="column" content="normal start" items="center stretch" gap={2}>
        <label htmlFor="isPublic" className="text-sm">
          <Trans>Make publicly accessible</Trans>
        </label>
        <input
          type="checkbox"
          name="isPublic"
          id="isPublic"
          defaultChecked={isPublic}
          onChange={(e) => {
            makePublic.mutate(e.target.checked);
          }}
        />
        {makePublic.isLoading && (
          <Spinner r={6} s={2} c="var(--palette-purple-0)" />
        )}
      </Box>
      {isPublic && (
        <Box>
          <LinkCopy
            value={`${window.location.origin}/p/${publicId}`}
            title={t`Public`}
            rawTitle="Public"
            ariaLabel="Copy Public Link"
          />
        </Box>
      )}
      <div className={"mt-5"}></div>
      {currentSharedEmails && (
        <>
          <Title>
            <Trans>Collaborator</Trans>
          </Title>
          <Box
            flow="column"
            content="normal start"
            items="center stretch"
            gap={2}
          >
            <Input
              type={"email"}
              ref={inputEmailRef}
              name="email"
              id={"email"}
              placeholder={"Email"}
              onKeyUp={(e) => {
                if (e.key === "Enter") {
                  const email = (e.target as HTMLTextAreaElement).value;
                  if (currentSharedEmails.includes(email)) {
                    return setOpenDuplicatedEmailDialog(true);
                  }
                  if (!isValidEmail(email)) {
                    return setOpenInvalidEmailDialog(true);
                  }

                  let cloneSharedEmails = [...currentSharedEmails];
                  cloneSharedEmails.push(email);
                  storeSharedEmails.mutate(cloneSharedEmails);

                  if (inputEmailRef.current) {
                    inputEmailRef.current.value = "";
                  }
                }
              }}
            />
            {storeSharedEmails.isLoading && (
              <Spinner r={6} s={2} c="var(--palette-purple-0)" />
            )}
          </Box>
          {currentSharedEmails.map((email, index) => {
            return (
              <div className={"flex gap-1 mt-5"} key={`email_${index}`}>
                <Trans key={index}>{email}</Trans>
                <Trash
                  size={16}
                  color={"red"}
                  cursor={"pointer"}
                  onClick={() => {
                    const filteredArray = currentSharedEmails.filter(function (
                      e
                    ) {
                      return e !== email;
                    });
                    storeSharedEmails.mutate(filteredArray);
                  }}
                />
              </div>
            );
          })}
          <Dialog.Root
            open={openDuplicatedEmailDialog}
            onOpenChange={setOpenDuplicatedEmailDialog}
          >
            <Dialog.Portal>
              <Overlay />
              <Dialog.Content className="data-[state=open]:animate-contentShow bg-background text-foreground dark:bg-foreground dark:text-background fixed top-[50%] left-[50%] max-h-[85vh] w-[90vw] max-w-[400px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-background p-4 shadow-lg focus:outline-none z-50 grid gap-3">
                <Dialog.Title className="text-lg font-bold">
                  <Trans>Duplicated Email</Trans>
                </Dialog.Title>
                <div className="flex gap-2 mt-6 justify-self-end">
                  <Dialog.Close asChild>
                    <Button2 leftIcon={<X size={16} />}>OK</Button2>
                  </Dialog.Close>
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
          <Dialog.Root
            open={openInvalidEmailDialog}
            onOpenChange={setOpenInvalidEmailDialog}
          >
            <Dialog.Portal>
              <Overlay />
              <Dialog.Content className="data-[state=open]:animate-contentShow bg-background text-foreground dark:bg-foreground dark:text-background fixed top-[50%] left-[50%] max-h-[85vh] w-[90vw] max-w-[400px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-background p-4 shadow-lg focus:outline-none z-50 grid gap-3">
                <Dialog.Title className="text-lg font-bold">
                  <Trans>Invalid Email</Trans>
                </Dialog.Title>
                <div className="flex gap-2 mt-6 justify-self-end">
                  <Dialog.Close asChild>
                    <Button2 leftIcon={<X size={16} />}>OK</Button2>
                  </Dialog.Close>
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        </>
      )}
    </Column>
  );
}
